import React, { createContext, useContext, useState, useCallback } from 'react'

export interface League {
  id: number
  name: string
  api_league_id: number
  shortName: string
  coverage: 'full' | 'standard' | 'basic'
  country: string
  type: 'league' | 'cup'
}

// Special "Live Today" pseudo-league that shows all fixtures from today
export const LIVE_TODAY_LEAGUE: League = {
  id: -1,
  name: 'Live Today',
  api_league_id: -1,
  shortName: 'Live',
  coverage: 'full',
  country: 'All',
  type: 'league',
}

// Helper to check if current selection is Live Today mode
export function isLiveTodayMode(league: League): boolean {
  return league.api_league_id === -1
}

// Coverage levels:
// - full: All features, highest data quality (EPL, La Liga)
// - standard: Most features, good data quality (Bundesliga, Serie A, Ligue 1)
// - basic: Core features only (Championship, lower leagues, cups)

// Country flags for display
export const COUNTRY_FLAGS: Record<string, string> = {
  England: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  Spain: '🇪🇸',
  Germany: '🇩🇪',
  Italy: '🇮🇹',
  France: '🇫🇷',
  Europe: '🇪🇺',
}

// Predefined leagues - these match API-Football league IDs
// Organized by country, then by tier/importance
export const LEAGUES: League[] = [
  // England - Top flight & Championship
  { id: 1, name: 'Premier League', shortName: 'EPL', api_league_id: 39, coverage: 'full', country: 'England', type: 'league' },
  { id: 2, name: 'Championship', shortName: 'Champ', api_league_id: 40, coverage: 'basic', country: 'England', type: 'league' },
  // England - Lower leagues
  { id: 7, name: 'League One', shortName: 'L1', api_league_id: 41, coverage: 'basic', country: 'England', type: 'league' },
  { id: 8, name: 'League Two', shortName: 'L2', api_league_id: 42, coverage: 'basic', country: 'England', type: 'league' },
  // England - Cups
  { id: 9, name: 'FA Cup', shortName: 'FA Cup', api_league_id: 45, coverage: 'basic', country: 'England', type: 'cup' },
  { id: 10, name: 'League Cup', shortName: 'EFL Cup', api_league_id: 48, coverage: 'basic', country: 'England', type: 'cup' },
  { id: 11, name: 'Community Shield', shortName: 'Shield', api_league_id: 528, coverage: 'basic', country: 'England', type: 'cup' },
  // England - Women's
  { id: 12, name: 'WSL', shortName: 'WSL', api_league_id: 44, coverage: 'basic', country: 'England', type: 'league' },
  // Spain - League & Cups
  { id: 3, name: 'La Liga', shortName: 'LaLiga', api_league_id: 140, coverage: 'full', country: 'Spain', type: 'league' },
  { id: 16, name: 'Copa del Rey', shortName: 'Copa', api_league_id: 143, coverage: 'basic', country: 'Spain', type: 'cup' },
  { id: 17, name: 'Supercopa de España', shortName: 'Supercopa', api_league_id: 556, coverage: 'basic', country: 'Spain', type: 'cup' },
  // Germany - League & Cups
  { id: 4, name: 'Bundesliga', shortName: 'BuLi', api_league_id: 78, coverage: 'standard', country: 'Germany', type: 'league' },
  { id: 18, name: 'DFB Pokal', shortName: 'DFB', api_league_id: 529, coverage: 'basic', country: 'Germany', type: 'cup' },
  { id: 19, name: 'DFL-Supercup', shortName: 'Supercup', api_league_id: 530, coverage: 'basic', country: 'Germany', type: 'cup' },
  // Italy - League & Cups
  { id: 5, name: 'Serie A', shortName: 'Serie A', api_league_id: 135, coverage: 'standard', country: 'Italy', type: 'league' },
  { id: 15, name: 'Coppa Italia', shortName: 'Coppa', api_league_id: 137, coverage: 'basic', country: 'Italy', type: 'cup' },
  { id: 20, name: 'Supercoppa Italiana', shortName: 'Supercoppa', api_league_id: 547, coverage: 'basic', country: 'Italy', type: 'cup' },
  // France - League & Cups
  { id: 6, name: 'Ligue 1', shortName: 'Ligue 1', api_league_id: 61, coverage: 'standard', country: 'France', type: 'league' },
  { id: 21, name: 'Coupe de France', shortName: 'CdF', api_league_id: 66, coverage: 'basic', country: 'France', type: 'cup' },
  { id: 22, name: 'Trophée des Champions', shortName: 'TdC', api_league_id: 526, coverage: 'basic', country: 'France', type: 'cup' },
  // UEFA European competitions
  { id: 13, name: 'UEFA Champions League', shortName: 'UCL', api_league_id: 2, coverage: 'standard', country: 'Europe', type: 'cup' },
  { id: 14, name: 'UEFA Europa League', shortName: 'UEL', api_league_id: 3, coverage: 'standard', country: 'Europe', type: 'cup' },
  { id: 23, name: 'UEFA Europa Conference League', shortName: 'UECL', api_league_id: 848, coverage: 'basic', country: 'Europe', type: 'cup' },
]

// Group leagues by country for menu display
export const LEAGUES_BY_COUNTRY: Record<string, League[]> = LEAGUES.reduce(
  (acc, league) => {
    if (!acc[league.country]) acc[league.country] = []
    acc[league.country].push(league)
    return acc
  },
  {} as Record<string, League[]>
)

// Top leagues for primary row display (major leagues only)
export const TOP_LEAGUES = LEAGUES.filter(
  (l) => [39, 140, 78, 135, 61].includes(l.api_league_id)
)

// English competitions for secondary row
export const ENGLISH_LEAGUES = LEAGUES.filter(
  (l) => l.country === 'England' && l.api_league_id !== 39
)

interface LeagueContextType {
  currentLeague: League
  setLeague: (league: League) => void
  leagues: League[]
}

const LeagueContext = createContext<LeagueContextType | null>(null)

const STORAGE_KEY = 'statscout_league'

export function LeagueProvider({ children }: { children: React.ReactNode }) {
  const [currentLeague, setCurrentLeague] = useState<League>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Check for Live Today mode
        if (parsed.api_league_id === -1) return LIVE_TODAY_LEAGUE
        // Validate stored league exists in our list
        const found = LEAGUES.find((l) => l.api_league_id === parsed.api_league_id)
        if (found) return found
      } catch {
        // Ignore parse errors
      }
    }
    // Default to Live Today
    return LIVE_TODAY_LEAGUE
  })

  const setLeague = useCallback((league: League) => {
    setCurrentLeague(league)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(league))
  }, [])

  return (
    <LeagueContext.Provider value={{ currentLeague, setLeague, leagues: LEAGUES }}>
      {children}
    </LeagueContext.Provider>
  )
}

export function useLeague() {
  const ctx = useContext(LeagueContext)
  if (!ctx) throw new Error('useLeague must be used within LeagueProvider')
  return ctx
}
